import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ValidationMessage from './validationMessage';
import Button from 'components/Button';
import {
  upperCaseCharValidation,
  specialCharValidation,
  oneNumberValidation,
  validatePassword,
  pwdEqualValidation,
  lowerCaseCharValidation,
} from 'utils/formValidators';
import cn from 'classnames';
import styles from './index.module.scss';
import Password from 'components/Forms/Fields/Password';

const BasePasswordPanel = ({
  submit,
  oldPassword,
  havingCancel,
  cancel,
  passwordPlaceholder,
  submitBtnText,
}) => {
  const [password, setPassword] = useState({});
  const [validation, setValidation] = useState([
    {
      name: 'hasUpperCase',
      value: false,
      message: 'at least 1 uppercase letter',
    },
    {
      name: 'hasLowerCase',
      value: false,
      message: 'at least 1 lowercase letter',
    },
    {
      name: 'hasSpecialChar',
      value: false,
      message: 'at least 1 special character',
    },
    {
      name: 'hasOneNumber',
      value: false,
      message: 'at least 1 number',
    },
    {
      name: 'hasMinLength',
      value: false,
      message: 'at least 6 characters',
    },
  ]);
  const [pwdrule, setPwdrule] = useState(false);
  const [pwdMissMatch, setPwdMissMatch] = useState(true);
  const [disableBtn, setDisableBtn] = useState(false);

  const validationMsgCheck = fieldValue => {
    const hasUpperCase = upperCaseCharValidation(fieldValue);
    const hasLowerCase = lowerCaseCharValidation(fieldValue);
    const hasSpecialChar = specialCharValidation(fieldValue);
    const hasOneNumber = oneNumberValidation(fieldValue);
    const hasMinLength = validatePassword(fieldValue);

    setValidation(
      validation.map(type => {
        switch (type.name) {
          case 'hasUpperCase':
            return {
              ...type,
              value: hasUpperCase,
            };
          case 'hasLowerCase':
            return {
              ...type,
              value: hasLowerCase,
            };

          case 'hasSpecialChar':
            return {
              ...type,
              value: hasSpecialChar,
            };

          case 'hasOneNumber':
            return {
              ...type,
              value: hasOneNumber,
            };

          case 'hasMinLength':
            return {
              ...type,
              value: hasMinLength,
            };
          default:
            return type;
        }
      }),
    );
  };

  const confirmPasswordCheck = (target, fieldValue) => {
    const validationResult = pwdEqualValidation(target, fieldValue);
    setPwdMissMatch(validationResult);
    setDisableBtn(validationResult);
  };

  const enablePwdRule = () => {
    setPwdrule(true);
  };
  const satisfyAllValidation = () => {
    const ruleSatified = validation.filter(item => {
      return item.value === true;
    });
    return ruleSatified.length === validation.length;
  };
  const disablePwdRule = () => {
    if (satisfyAllValidation()) {
      setPwdrule(false);
    }
  };

  const updatePassword = e => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    if (fieldName === 'newPassword') {
      validationMsgCheck(e.target.value);
      confirmPasswordCheck(password.confirmPassword, e.target.value);
    } else if (fieldName === 'confirmPassword') {
      confirmPasswordCheck(password.newPassword, e.target.value);
    }

    setPassword({
      ...password,
      [fieldName]: fieldValue,
    });
  };

  const submitBtnDisable = () => {
    if (satisfyAllValidation() && disableBtn) {
      return false;
    } else {
      return true;
    }
  };
  const submitPwd = e => {
    e.preventDefault();
    if (oldPassword) {
      submit(password.oldPassword, password.newPassword);
    } else {
      submit(password.newPassword);
    }
  };

  return (
    <form className={styles.formContent}>
      {oldPassword && (
        <Password
          id="oldPassword"
          fieldName="oldPassword"
          placeholder="Current Password"
          onChange={updatePassword}
        />
      )}
      <Password
        id="newPassword"
        fieldName="newPassword"
        placeholder={passwordPlaceholder}
        onChange={updatePassword}
        onFocus={enablePwdRule}
      />
      <Password
        id="confirmPassword"
        fieldName="confirmPassword"
        placeholder="Confirm Password"
        onChange={updatePassword}
        onFocus={disablePwdRule}
        error={!pwdMissMatch ? 'Passwords must match.' : undefined}
      />
      <div className={cn(styles.pwdRule, { [styles.hideIcon]: !pwdrule })}>
        <p> Password must contain: </p>
        {validation.map((item, k) => (
          <ValidationMessage key={k} validation={item} />
        ))}
      </div>
      <div className={styles.buttonRow}>
        {havingCancel && (
          <Button
            type="button"
            text="Cancel"
            color="secondary"
            onClick={cancel}
            theme="light"
          />
        )}
        <Button
          type="submit"
          text={submitBtnText}
          onClick={submitPwd}
          disabled={submitBtnDisable()}
          color="primary"
        />
      </div>
    </form>
  );
};
BasePasswordPanel.propTypes = {
  cancel: PropTypes.func,
  oldPassword: PropTypes.bool,
  havingCancel: PropTypes.bool,
  passwordPlaceholder: PropTypes.string,
  submit: PropTypes.func,
  submitBtnText: PropTypes.string,
};
BasePasswordPanel.defaultProps = {
  oldPassword: false,
  havingCancel: false,
  passwordPlaceholder: 'Password',
};

export default BasePasswordPanel;
